import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  acknowledgePolicyToken,
  fetchPendingPolicyAck,
  fetchPublishedPrivacyPolicy,
  validatePolicyAckToken,
} from "@/services/policyAckService";

export function usePublishedPrivacyPolicy() {
  return useQuery({
    queryKey: ["published-privacy-policy"],
    queryFn: fetchPublishedPrivacyPolicy,
    staleTime: 5 * 60_000,
  });
}

export function usePendingPolicyAck(enabled = true) {
  return useQuery({
    queryKey: ["pending-policy-ack"],
    queryFn: fetchPendingPolicyAck,
    enabled,
    refetchInterval: 60_000,
  });
}

/**
 * Use Policy Ack Actions.
 */
export function usePolicyAckActions() {
  const queryClient = useQueryClient();

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["pending-policy-ack"] });
    await queryClient.invalidateQueries({ queryKey: ["published-privacy-policy"] });
  }, [queryClient]);

  const acknowledge = useCallback(
    async (token: string, mfaCode: string) => {
      const result = await acknowledgePolicyToken(token, mfaCode);
      await invalidate();
      return result;
    },
    [invalidate]
  );

  return useMemo(
    () => ({
      validateToken: validatePolicyAckToken,
      acknowledge,
    }),
    [acknowledge]
  );
}
